<template>
  <mc-dialog 
    ref="viewSaveDialog"
    class="filter-view-save-dialog"
    :submit-button-text="submitButtonText"
    submit-button-class="primary-submit-button"
    cancel-button-text="Cancel"
    cancel-button-class="primary-cancel-button"
    :close-by-submit-click=false
    @submit="handleSubmitButtonClick"
    @cancel="handleCancelButtonClick">

    <template #header>
      <h3 class="title-dialog">{{ dialogTitle }}</h3>
    </template>

    <section class="body-content">
      <mc-clearable-input ref="viewNameInput"
        class="filter-input view-name" 
        :input-value="filterStateToSave.name" 
        label="View Name" 
        placeholder="Enter Name..." 
        :clearable="false"
        :error-message="nameErrorMessage"
        @input="validateViewName($event)"
        @change="handleNameChange($event)"/>
      <div v-if="dialogMode !== FilterViewDialogMode.edit" class="chips-wrapper">
        <filter-chips-item v-for="item in filterStateToSave.chips" 
          :key="item.property" 
          :item="item" 
          :disabled="isChipsRemovalDisabled" 
          @remove="handleChipsItemRemove($event)"/>
      </div>
    </section>

  </mc-dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, toRefs } from 'vue';
import _ from 'lodash';
import FilterChipsItem from '@/components/shared/filters/FilterChips/FilterChipsItem.vue';
import McClearableInput from '@/components/ui/McClearableInput.vue';
import McDialog from '@/components/ui/McDialog/McDialog.vue';
import { useQuickFilterStore } from '@/store/quickFilterStore';
import { useToastStore } from '@/store/toastStore';
import { useTopbarFilterStore } from '@/store/topbarFilterStore';
import { UserActions } from '@/models/appLogger.model';
import { IFilterChipsEventData, IFilterState, IFilterViewModel } from '@/models/filter.model';
import { FilterViewDialogMode, IQuickFilterOptions, IQuickFilterResponse, IQuickFilterViewModel } from '@/models/quickFilter.model';
import { filterService } from '@/services/filter';
import { quickFilterDataService } from '@/services/QuickFilterApi';
import { appLogger } from '@/services/AppLogger';
import { getItemDefaultCopyName } from '@/helpers/shared';

interface IFilterViewSaveDialogProps {
  filterState?: IFilterState | IQuickFilterViewModel;
  dialogMode?: FilterViewDialogMode;
  isObjectionsFeatureEnabled?: boolean;
  isScorecardsFeatureEnabled?: boolean;
}

const props = withDefaults(defineProps<IFilterViewSaveDialogProps>(), {
  filterState: () => ({} as IFilterState),
  dialogMode: FilterViewDialogMode.create,
  isObjectionsFeatureEnabled: false,
  isScorecardsFeatureEnabled: false,
});

const { 
  filterState, 
  dialogMode, 
  isObjectionsFeatureEnabled,
  isScorecardsFeatureEnabled
} = toRefs(props);

const quickFilterStore = useQuickFilterStore();
const filterStore = useTopbarFilterStore();
const toastStore = useToastStore();

const filterData = ref<IFilterViewModel | undefined>(_.cloneDeep(await filterStore.filterDataAsync));
const existingViews = computed(() => quickFilterStore.quickFilters);

const viewSaveDialog = ref();
const viewNameInput = ref();
const dialogTitle = ref<string>('');
const submitButtonText = ref<string>('');

const filterStateToSave = ref<IFilterState | IQuickFilterViewModel>(_.cloneDeep(filterState.value));
const isChipsRemovalDisabled = computed(() => filterStateToSave.value.chips?.length <= 1);
const nameErrorMessage = ref<string | undefined>(undefined);
const nameErrorMessageAlreadyExists = "View name already exists, please enter a unique name";

const emits = defineEmits(["save", "cancel"]);

const openDialog = () => {
  setDialogAttributes();
  viewSaveDialog.value?.openDialog();
  nextTick(() => viewNameInput.value?.selectValue());
};

const closeDialog = () => {
  viewSaveDialog.value?.closeDialog();
  resetToDefaultState();
};

const handleSubmitButtonClick = () => {
  validateViewName(filterStateToSave.value.name);
  if (nameErrorMessage.value) return;

  const requestOptions = {
    id: dialogMode.value === FilterViewDialogMode.edit
      ? filterStateToSave.value.id
      : undefined,
    name: filterStateToSave.value.name,
    folderId: filterStateToSave.value.folderId,
    data: filterStateToSave.value.filter,
  } as IQuickFilterOptions;

  appLogger.logUserAction(
    dialogMode.value === FilterViewDialogMode.edit
      ? UserActions.viewFilterUpdate
      : UserActions.viewFilterCreate, 
    { 
      view: requestOptions.name, 
      folderId: requestOptions.folderId ?? 'root' 
    });

  quickFilterDataService.upsertAsync(requestOptions)
    .then((response: IQuickFilterResponse) => {
      const quickFilter = {
        id: response.id,
        name: response.name,
        folderId: response.folderId,
        filter: _.cloneDeep(filterStateToSave.value.filter),
        chips: _.cloneDeep(filterStateToSave.value.chips),
      }

      if (dialogMode.value === FilterViewDialogMode.edit) {
        quickFilterStore.updateQuickFilter(quickFilter);
        toastStore.showItemSuccessfullyRenamed(filterState.value?.name ?? "", response.name);
      }
      else {
        toastStore.showItemSuccessfullyCreated(response.name, "New view &nbsp;");
        quickFilterStore.addQuickFilter(quickFilter);
        quickFilterStore.setSelectedQuickFilter(quickFilter);
      }

      emits("save", quickFilter);
      closeDialog();
    })
    .catch((error: any) => {
      if (error.response?.status === 409) {
        nameErrorMessage.value = nameErrorMessageAlreadyExists;
      }
      else {
        throw error;
      }
    });
};

const handleCancelButtonClick = () => {
  resetToDefaultState();
  emits("cancel");
};

const handleNameChange = (newValue: string | undefined) => {
  validateViewName(newValue);
  filterStateToSave.value.name = newValue;
};

const handleChipsItemRemove = (eventData: IFilterChipsEventData) => {
  if (!eventData || !eventData.property || isChipsRemovalDisabled.value) {
    return;
  }

  filterStateToSave.value.filter = filterService.clearFilterProperty(filterStateToSave.value.filter, eventData);
  const propertyValue = filterStateToSave.value.filter[eventData.property];
  filterStateToSave.value.chips = filterService.updateChipsForFilterProperty(
    filterStateToSave.value.chips,
    eventData.property,
    propertyValue,
    filterData.value,
    isObjectionsFeatureEnabled.value,
    isScorecardsFeatureEnabled.value);
};

const validateViewName = (nameValue: string | undefined) => {
  const nameMaxLength = 40;
  nameValue = nameValue?.trim();

  if (!nameValue) {
    nameErrorMessage.value = "Please enter a name for this View";
  } 
  else if (nameValue.length > nameMaxLength) {
    nameErrorMessage.value = `View name must not be longer than ${nameMaxLength} characters`;
  }
  else if (existingViews.value.some((filter) => filter.name.toLowerCase() === nameValue.toLowerCase())) {
    nameErrorMessage.value = nameErrorMessageAlreadyExists;
  }
  else if (!(/^[a-z0-9 ]+$/gi.test(nameValue))){
    nameErrorMessage.value = "Only numbers and letters allowed";
  }
  else {
    nameErrorMessage.value = undefined;
  }
};

const setDialogAttributes = () => {
  filterStateToSave.value = _.cloneDeep(filterState.value);

  switch (dialogMode.value) {
    case FilterViewDialogMode.create:
      dialogTitle.value = "Create New View";
      submitButtonText.value = "Create View";
      filterStateToSave.value.name = undefined;
      break;
    case FilterViewDialogMode.edit:
      dialogTitle.value = "Rename View";
      submitButtonText.value = "Save";
      break;
    case FilterViewDialogMode.copy:
      dialogTitle.value = `Create a copy of “${filterStateToSave.value.name}”`;
      filterStateToSave.value.name = getItemDefaultCopyName(
        filterStateToSave.value?.name ?? '', 
        existingViews.value.map(view => view.name)
      );
      submitButtonText.value = "Save";
      break
    default:
      submitButtonText.value = "Save";
      break;
  }
};

const resetToDefaultState = () => {
  filterStateToSave.value = _.cloneDeep(filterState.value);
  nameErrorMessage.value = undefined;
};

defineExpose({
  openDialog,
  closeDialog
});
</script>

<style scoped lang="scss">
.filter-view-save-dialog {
  --dialog-width: 427px;
  --dialog-padding: 36px 27px 24px;
  --dialog-content-horizontal-padding: 5px;
  --dialog-actions-section-margin: 0;
  --dialog-action-buttons-padding: 0 8px;

  min-height: 211px;
  max-height: 600px;

  .title-dialog {
    padding: 0 var(--dialog-content-horizontal-padding);
  }

  .body-content {
    display: flex;
    flex-direction: column;
    padding: 24px var(--dialog-content-horizontal-padding) 32px;
    gap: 24px;
  }

  .view-name {
    --clearable-input-color: var(--colour-text-neutral-subtle);
  }

  .chips-wrapper {
    display: flex;
    flex-wrap: wrap;
    overflow-y: auto;
    max-height: 300px;
    gap: 8px;
  }

  :deep(.primary-submit-button) {
    --button-width: 120px;
  }

  :deep(.primary-cancel-button) {
    --button-width: 100px;
  }
}
</style>